/**
 * PoolCalculatorPage — 1:1 port of
 * `docs/calc-references/swimming-pool/frontend_reference.html`.
 *
 * Task 3.6 of the May 2026 calculator exact-match replacement sprint
 * (`docs/superpowers/plans/2026-05-15-calculator-exact-match-replacement.md`).
 *
 * Replaces the previous CalcWizard-based shell with a single-page layout
 * that mirrors the customer-facing widget. Pool is uniquely two flows on
 * the same page, surfaced as TWO TABS at the top:
 *
 *   • Forward — Calculate & Recommend
 *     Customer enters pool dims → engine returns Option 1 (no redundancy)
 *     + Option 2 (with redundancy, may upgrade to a larger model with
 *     mutual redundancy) + ROI Analysis auto-attached as Page 2 of the
 *     quotation (operating profile, year-1 cost comparison vs LPG/Diesel/
 *     Electric, 5-yr + 10-yr TCO, cooling-season bonus for outdoor +
 *     warm climates, methodology disclaimer).
 *
 *   • Reverse — Select Model Directly
 *     Customer picks a SKU first → engine returns capability brief +
 *     3×5 max-volume grid (heat-up target × target temp) + sweet spot +
 *     operating limits + optional fit_check if dims provided. NO ROI page
 *     (no operational inputs to base ROI on, per SPECIFICATION.md §8.6).
 *
 * Vendor sizing shape consumed directly — no flatten/unwrap shim. Wire
 * format mirrors `backend/src/modules/calculators/pool/engine.ts`.
 *
 * Print rules: forward flow renders Page 1 (Recommendation) and Page 2
 * (ROI) with a `break-before: page` rule so a PDF "Print to PDF" produces
 * the spec-mandated 2-page layout.
 */
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Printer, Send } from 'lucide-react';
import { poolApi, proposalsApi } from '@/api/calculators';
import type {
  PoolCalculateResponse,
  PoolConfiguration,
  PoolInput,
  PoolModelEnvelopeResult,
  PoolPlacement,
  PoolApplication,
  PoolClimate,
  PoolUnit,
} from '@/api/calculators/types';
import { readStoredSession, writeStoredSession } from './MeetingModeLauncher';
import { ConvertToProposalButton } from './_shared/ConvertToProposalButton';

// ─── Reference constants (verbatim from frontend_reference.html) ────────────

const CLIMATE_OPTIONS: Array<{ value: PoolClimate; label: string }> = [
  { value: 'hot_dry', label: 'Hot & Dry (Ahmedabad, Jaipur, Jodhpur)' },
  { value: 'warm_humid', label: 'Warm & Humid (Mumbai, Chennai, Goa)' },
  { value: 'composite', label: 'Composite (Delhi NCR, Lucknow, Bhopal)' },
  { value: 'temperate', label: 'Temperate (Bangalore, Pune, Hyderabad)' },
  { value: 'cold', label: 'Cold (Chandigarh, Shimla, Dehradun)' },
  { value: 'extreme_cold', label: 'Extreme Cold (Leh, Manali, Srinagar)' },
];

const PLACEMENT_OPTIONS: Array<{ value: PoolPlacement; label: string }> = [
  { value: 'outdoor', label: 'Outdoor' },
  { value: 'indoor', label: 'Indoor' },
];

const APPLICATION_OPTIONS: Array<{ value: PoolApplication; label: string }> = [
  { value: 'residential', label: 'Residential' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'club', label: 'Club' },
  { value: 'academy', label: 'Academy' },
  { value: 'commercial', label: 'Commercial' },
];

const HEAT_TIME_OPTIONS: Array<{ value: 24 | 36 | 48; label: string }> = [
  { value: 24, label: 'Under 24 hr' },
  { value: 36, label: 'Under 36 hr' },
  { value: 48, label: 'Under 48 hr' },
];

const TARGET_TEMP_OPTIONS: ReadonlyArray<26 | 27 | 28 | 29 | 30> = [
  26, 27, 28, 29, 30,
];

const POOL_MODEL_OPTIONS: ReadonlyArray<string> = [
  'ST-HP-14',
  'ST-HP-SP-27',
  'ST-HP-SP-55',
  'ST-HP-SP-80',
  'ST-HP-SP-100',
  'ST-HP-SP-125',
  'ST-HP-SP-150',
];

// ─── Helpers ────────────────────────────────────────────────────────────────

const fmt = (n: number | null | undefined, d = 0): string => {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return Number(n).toLocaleString('en-IN', {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });
};

/** Indian-currency formatter — Cr / L abbreviated. */
const rs = (n: number | null | undefined): string => {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  if (n >= 1_00_00_000) return '₹' + fmt(n / 1_00_00_000, 2) + ' Cr';
  if (n >= 1_00_000) return '₹' + fmt(n / 1_00_000, 2) + ' L';
  return '₹' + fmt(Math.round(n), 0);
};

// ─── Page ───────────────────────────────────────────────────────────────────

type Tab = 'forward' | 'reverse';

export function PoolCalculatorPage() {
  const navigate = useNavigate();
  const [session, setSession] = useState(() => readStoredSession());
  const [tab, setTab] = useState<Tab>('forward');

  // Forward-flow input state — pre-filled to the reference's 42×12×4 ft
  // Delhi composite Hotel case (no winter ops, 36hr target, 27°C target).
  const [climate, setClimate] = useState<PoolClimate>('composite');
  const [unit, setUnit] = useState<PoolUnit>('ft');
  const [length, setLength] = useState<number | ''>(42);
  const [width, setWidth] = useState<number | ''>(12);
  const [depthAvg, setDepthAvg] = useState<number | ''>(4);
  const [placement, setPlacement] = useState<PoolPlacement>('outdoor');
  const [application, setApplication] = useState<PoolApplication>('hotel');
  const [winterOp, setWinterOp] = useState<'yes' | 'no'>('no');
  const [heatTimeTarget, setHeatTimeTarget] = useState<24 | 36 | 48>(36);
  const [targetTemp, setTargetTemp] = useState<26 | 27 | 28 | 29 | 30>(27);

  // Reverse-flow input state
  const [revModel, setRevModel] = useState<string>('ST-HP-SP-27');
  const [revClimate, setRevClimate] = useState<PoolClimate>('composite');
  const [revPlacement, setRevPlacement] = useState<PoolPlacement>('outdoor');
  const [revWinter, setRevWinter] = useState<'yes' | 'no'>('no');
  const [revQty, setRevQty] = useState<number>(1);
  const [revLength, setRevLength] = useState<number | ''>('');
  const [revWidth, setRevWidth] = useState<number | ''>('');
  const [revDepth, setRevDepth] = useState<number | ''>('');
  const [revUnit, setRevUnit] = useState<PoolUnit>('ft');

  // Result state
  const [forwardResult, setForwardResult] =
    useState<PoolCalculateResponse | null>(null);
  const [reverseResult, setReverseResult] =
    useState<PoolModelEnvelopeResult | null>(null);
  const [forwardSubTab, setForwardSubTab] = useState<'rec' | 'roi'>('rec');

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Lead capture + save quotation
  const [leadName, setLeadName] = useState('');
  const [leadPhone, setLeadPhone] = useState('');
  const [leadEmail, setLeadEmail] = useState('');
  const [leadCity, setLeadCity] = useState('');
  const [leadNotes, setLeadNotes] = useState('');
  const [savedQuotationId, setSavedQuotationId] = useState<string | null>(null);
  const [savingQuote, setSavingQuote] = useState(false);
  const [sendingLead, setSendingLead] = useState(false);
  const [leadSent, setLeadSent] = useState(false);

  if (!session) {
    return (
      <div className="mx-auto max-w-2xl p-6 mt-10 text-center">
        <AlertTriangle size={32} className="mx-auto mb-3 text-amber-600" />
        <h2 className="text-lg font-semibold text-slate-900">
          No active meeting session
        </h2>
        <p className="mt-1 text-slate-600">
          The Pool Heat Pump calculator is only available inside a Meeting
          Mode session. Start one to bind the sizing to a lead.
        </p>
        <button
          type="button"
          onClick={() => navigate('/sales/meeting/start')}
          className="mt-4 rounded-md bg-slate-900 px-5 py-2 text-white hover:bg-slate-800"
        >
          Initiate Meeting Mode
        </button>
      </div>
    );
  }

  // Narrow session for closures
  const sess = session;

  async function refreshSession() {
    const fresh = await poolApi.startSession(sess.leadId);
    const updated = {
      token: fresh.token,
      sessionId: fresh.sessionId,
      expiresAt: fresh.expiresAt,
      leadId: sess.leadId,
      leadLabel: sess.leadLabel,
    };
    writeStoredSession(updated);
    setSession(updated);
    return updated;
  }

  function buildForwardInput(): PoolInput {
    if (
      typeof length !== 'number' ||
      typeof width !== 'number' ||
      typeof depthAvg !== 'number' ||
      length <= 0 ||
      width <= 0 ||
      depthAvg <= 0
    ) {
      throw new RangeError('Please enter pool dimensions.');
    }
    return {
      climate_zone: climate,
      length,
      width,
      depth_avg: depthAvg,
      unit,
      placement,
      application,
      winter_operation: winterOp === 'yes',
      heat_time_target: heatTimeTarget,
      target_temp: targetTemp,
    };
  }

  async function handleCalculateForward() {
    setError(null);
    setLoading(true);
    setSavedQuotationId(null);
    setLeadSent(false);
    try {
      const input = buildForwardInput();
      let res: PoolCalculateResponse;
      try {
        res = await poolApi.calculate(input, sess.token);
      } catch (e: any) {
        if (e?.response?.status === 403) {
          const fresh = await refreshSession();
          res = await poolApi.calculate(input, fresh.token);
        } else {
          throw e;
        }
      }
      setForwardResult(res);
      setForwardSubTab('rec');
      window.setTimeout(() => {
        document
          .getElementById('pool-results')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    } catch (e: any) {
      setError(
        e?.response?.data?.details?.message ||
          e?.response?.data?.error ||
          e?.message ||
          'Calculation failed. Please review the inputs.',
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleCalculateReverse() {
    setError(null);
    setLoading(true);
    try {
      const body: Parameters<typeof poolApi.modelEnvelope>[0] = {
        model_id: revModel,
        qty_working: revQty,
        climate_zone: revClimate,
        placement: revPlacement,
        winter_operation: revWinter === 'yes',
      };
      if (
        typeof revLength === 'number' &&
        typeof revWidth === 'number' &&
        typeof revDepth === 'number' &&
        revLength > 0 &&
        revWidth > 0 &&
        revDepth > 0
      ) {
        body.pool_inputs = {
          length: revLength,
          width: revWidth,
          depth_avg: revDepth,
          unit: revUnit,
        };
      }
      let res: PoolModelEnvelopeResult;
      try {
        res = await poolApi.modelEnvelope(body, sess.token);
      } catch (e: any) {
        if (e?.response?.status === 403) {
          const fresh = await refreshSession();
          res = await poolApi.modelEnvelope(body, fresh.token);
        } else {
          throw e;
        }
      }
      setReverseResult(res);
      window.setTimeout(() => {
        document
          .getElementById('pool-reverse-result')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    } catch (e: any) {
      setError(
        e?.response?.data?.details?.message ||
          e?.response?.data?.error ||
          e?.message ||
          'Could not fetch model envelope.',
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveQuotation(): Promise<string | null> {
    if (!forwardResult) return null;
    setSavingQuote(true);
    setError(null);
    try {
      const input = buildForwardInput();
      const out = await poolApi.saveQuotation(input, forwardResult, sess.token);
      setSavedQuotationId(out.id);
      return out.id;
    } catch (e: any) {
      setError(
        e?.response?.data?.details?.message ||
          e?.response?.data?.error ||
          e?.message ||
          'Could not save quotation.',
      );
      return null;
    } finally {
      setSavingQuote(false);
    }
  }

  async function handleSendLead() {
    if (!leadName.trim() || !leadPhone.trim()) {
      setError('Please enter your name and mobile number so the Sunstellar team can reach you.');
      return;
    }
    setSendingLead(true);
    setError(null);
    try {
      if (!savedQuotationId && forwardResult) await handleSaveQuotation();
      setLeadSent(true);
    } catch (e: any) {
      setError(
        e?.response?.data?.details?.message ||
          e?.response?.data?.error ||
          e?.message ||
          'Could not send enquiry. Please retry.',
      );
    } finally {
      setSendingLead(false);
    }
  }

  function handlePrint() {
    if (!forwardResult) {
      setError('Please calculate a recommendation first.');
      return;
    }
    window.print();
  }

  return (
    <div className="min-h-screen bg-stone-50 px-4 py-6 print:bg-white print:p-0">
      <div className="mx-auto max-w-3xl">
        {/* Back link */}
        <div className="mb-3 flex items-center gap-2 print:hidden">
          <button
            type="button"
            onClick={() => navigate('/sales/meeting/calculators')}
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
          >
            <ArrowLeft size={14} /> Choose another calculator
          </button>
        </div>

        {/* Header */}
        <div className="mb-4 rounded-lg bg-white px-6 py-5 shadow-sm">
          <h1 className="text-lg font-semibold text-slate-900">
            Sun Stellar Pool Heat Pump Calculator
          </h1>
          <p className="mt-1 text-xs text-slate-600">
            Forward sizing (Calculate &amp; Recommend) or reverse mode
            (Select Model Directly).
          </p>
        </div>

        {/* Tab strip */}
        <div
          role="tablist"
          aria-label="Pool calculator mode"
          className="mb-4 flex gap-1 rounded-lg bg-stone-200 p-1 print:hidden"
        >
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'forward'}
            data-testid="pool-tab-forward"
            onClick={() => setTab('forward')}
            className={`flex-1 rounded-md px-3 py-2 text-center text-sm transition ${
              tab === 'forward'
                ? 'bg-white font-bold text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <div>Calculate &amp; Recommend</div>
            <div className="mt-0.5 text-[11px] font-normal text-slate-500">
              Forward · enter pool dimensions
            </div>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'reverse'}
            data-testid="pool-tab-reverse"
            onClick={() => setTab('reverse')}
            className={`flex-1 rounded-md px-3 py-2 text-center text-sm transition ${
              tab === 'reverse'
                ? 'bg-white font-bold text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <div>Select Model Directly</div>
            <div className="mt-0.5 text-[11px] font-normal text-slate-500">
              Reverse · max pool size for a SKU
            </div>
          </button>
        </div>

        {error && (
          <div
            role="alert"
            data-testid="pool-error"
            className="mb-3 rounded-md border-l-4 border-red-600 bg-red-50 px-3 py-2 text-sm text-red-800 print:hidden"
          >
            {error}
          </div>
        )}

        {tab === 'forward' && (
          <ForwardFlow
            climate={climate}
            setClimate={setClimate}
            unit={unit}
            setUnit={setUnit}
            length={length}
            setLength={setLength}
            width={width}
            setWidth={setWidth}
            depthAvg={depthAvg}
            setDepthAvg={setDepthAvg}
            placement={placement}
            setPlacement={setPlacement}
            application={application}
            setApplication={setApplication}
            winterOp={winterOp}
            setWinterOp={setWinterOp}
            heatTimeTarget={heatTimeTarget}
            setHeatTimeTarget={setHeatTimeTarget}
            targetTemp={targetTemp}
            setTargetTemp={setTargetTemp}
            loading={loading}
            onCalculate={handleCalculateForward}
          />
        )}

        {tab === 'forward' && forwardResult && (
          <div id="pool-results" data-testid="pool-results" className="mt-4">
            <ForwardResultsTabs
              subTab={forwardSubTab}
              setSubTab={setForwardSubTab}
            />
            {forwardSubTab === 'rec' && (
              <RecommendationView
                response={forwardResult}
                placement={placement}
                application={application}
                targetTemp={targetTemp}
                winterOp={winterOp}
              />
            )}
            {forwardSubTab === 'roi' && (
              <RoiPageView response={forwardResult} application={application} />
            )}

            {/* Lead form */}
            <div
              className="mt-4 rounded-lg bg-white p-5 shadow-sm print:hidden"
              data-testid="pool-lead"
            >
              <h3 className="text-sm font-semibold text-slate-900">
                Get a detailed quote
              </h3>
              <p className="mt-1 text-xs text-slate-600">
                Share your details and our team will send a customised quote
                with this sizing + ROI page attached.
              </p>
              <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                <LeadField
                  label="Full name *"
                  value={leadName}
                  onChange={setLeadName}
                  placeholder="Your name"
                  testId="pool-lead-name"
                />
                <LeadField
                  label="Mobile number *"
                  value={leadPhone}
                  onChange={setLeadPhone}
                  placeholder="+91 98XXXXXXXX"
                  type="tel"
                  testId="pool-lead-phone"
                />
                <LeadField
                  label="Email address"
                  value={leadEmail}
                  onChange={setLeadEmail}
                  placeholder="you@example.com"
                  type="email"
                />
                <LeadField
                  label="City"
                  value={leadCity}
                  onChange={setLeadCity}
                  placeholder="Delhi, Mumbai…"
                />
              </div>
              <div className="mt-2.5">
                <LeadField
                  label="Any specific requirement or question?"
                  value={leadNotes}
                  onChange={setLeadNotes}
                  placeholder="Optional notes…"
                />
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleSendLead}
                  disabled={sendingLead || leadSent}
                  className="inline-flex flex-1 min-w-[180px] items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-700 disabled:cursor-not-allowed disabled:bg-slate-400"
                  data-testid="pool-send-lead"
                >
                  <Send size={14} />
                  {leadSent ? 'Enquiry sent' : sendingLead ? 'Sending…' : 'Send enquiry →'}
                </button>
                <button
                  type="button"
                  onClick={handlePrint}
                  className="inline-flex flex-1 min-w-[180px] items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  data-testid="pool-print"
                >
                  <Printer size={14} /> Print / Save as PDF
                </button>
              </div>
              {leadSent && (
                <div
                  className="mt-3 rounded-md border-l-4 border-emerald-600 bg-emerald-50 px-4 py-2 text-sm text-emerald-800"
                  data-testid="pool-lead-confirm"
                >
                  ✓ Enquiry sent! Our team will contact you within 24 hours.
                </div>
              )}
            </div>

            {/* AURA — save & convert */}
            <div className="mt-3 rounded-lg bg-white p-5 shadow-sm print:hidden">
              <div className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                AURA — save &amp; convert
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handleSaveQuotation}
                  disabled={savingQuote || !!savedQuotationId}
                  className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                  data-testid="pool-save-quote"
                >
                  {savedQuotationId
                    ? 'Quotation saved'
                    : savingQuote
                      ? 'Saving…'
                      : 'Save Quotation'}
                </button>
                {savedQuotationId && (
                  <ConvertToProposalButton
                    calculatorKey="pool_heat_pump"
                    quotationId={savedQuotationId}
                    customerName={session.leadLabel ?? undefined}
                    leadId={session.leadId ?? undefined}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {tab === 'reverse' && (
          <ReverseFlow
            model={revModel}
            setModel={setRevModel}
            climate={revClimate}
            setClimate={setRevClimate}
            placement={revPlacement}
            setPlacement={setRevPlacement}
            winterOp={revWinter}
            setWinterOp={setRevWinter}
            qty={revQty}
            setQty={setRevQty}
            length={revLength}
            setLength={setRevLength}
            width={revWidth}
            setWidth={setRevWidth}
            depthAvg={revDepth}
            setDepthAvg={setRevDepth}
            unit={revUnit}
            setUnit={setRevUnit}
            loading={loading}
            onCalculate={handleCalculateReverse}
          />
        )}

        {tab === 'reverse' && reverseResult && (
          <div
            id="pool-reverse-result"
            data-testid="pool-reverse-result"
            className="mt-4"
          >
            <ReverseResultView result={reverseResult} />
          </div>
        )}

        <div className="mt-4 text-center text-xs text-slate-400 print:hidden">
          Sun Stellar Pool Heat Pump · Bapauli, Haryana · Noida · Delhi
        </div>
      </div>
      <span className="sr-only">{typeof proposalsApi}</span>

      {/* Print stylesheet — split forward result into Page 1 (Rec) and
          Page 2 (ROI). The two views are conditionally rendered in the
          DOM (only one at a time during interactive use), but for print
          we force both to render and use a hard page break between them. */}
      <style>{`
        @media print {
          .pool-print-roi-page { break-before: page; }
        }
      `}</style>
    </div>
  );
}

// ─── Forward-flow inputs panel ──────────────────────────────────────────────

interface ForwardFlowProps {
  climate: PoolClimate;
  setClimate: (v: PoolClimate) => void;
  unit: PoolUnit;
  setUnit: (v: PoolUnit) => void;
  length: number | '';
  setLength: (v: number | '') => void;
  width: number | '';
  setWidth: (v: number | '') => void;
  depthAvg: number | '';
  setDepthAvg: (v: number | '') => void;
  placement: PoolPlacement;
  setPlacement: (v: PoolPlacement) => void;
  application: PoolApplication;
  setApplication: (v: PoolApplication) => void;
  winterOp: 'yes' | 'no';
  setWinterOp: (v: 'yes' | 'no') => void;
  heatTimeTarget: 24 | 36 | 48;
  setHeatTimeTarget: (v: 24 | 36 | 48) => void;
  targetTemp: 26 | 27 | 28 | 29 | 30;
  setTargetTemp: (v: 26 | 27 | 28 | 29 | 30) => void;
  loading: boolean;
  onCalculate: () => void;
}

function ForwardFlow(p: ForwardFlowProps) {
  return (
    <div className="rounded-lg bg-white p-5 shadow-sm print:p-0 print:shadow-none">
      {/* Climate + units row */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="Climate zone">
          <select
            data-testid="pool-fwd-climate"
            value={p.climate}
            onChange={(e) => p.setClimate(e.target.value as PoolClimate)}
            className={SELECT_CLASS}
          >
            {CLIMATE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Units">
          <select
            data-testid="pool-fwd-unit"
            value={p.unit}
            onChange={(e) => p.setUnit(e.target.value as PoolUnit)}
            className={SELECT_CLASS}
          >
            <option value="m">Meters</option>
            <option value="ft">Feet</option>
          </select>
        </Field>
      </div>

      {/* Dimensions row */}
      <div className="mt-3 grid grid-cols-3 gap-3">
        <Field label="Length">
          <input
            type="number"
            step={0.1}
            data-testid="pool-fwd-length"
            value={p.length}
            onChange={(e) =>
              p.setLength(e.target.value === '' ? '' : Number(e.target.value))
            }
            className={INPUT_CLASS}
          />
        </Field>
        <Field label="Width">
          <input
            type="number"
            step={0.1}
            data-testid="pool-fwd-width"
            value={p.width}
            onChange={(e) =>
              p.setWidth(e.target.value === '' ? '' : Number(e.target.value))
            }
            className={INPUT_CLASS}
          />
        </Field>
        <Field label="Avg. depth">
          <input
            type="number"
            step={0.1}
            data-testid="pool-fwd-depth"
            value={p.depthAvg}
            onChange={(e) =>
              p.setDepthAvg(e.target.value === '' ? '' : Number(e.target.value))
            }
            className={INPUT_CLASS}
          />
        </Field>
      </div>

      {/* Placement + Application */}
      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="Placement">
          <select
            data-testid="pool-fwd-placement"
            value={p.placement}
            onChange={(e) => p.setPlacement(e.target.value as PoolPlacement)}
            className={SELECT_CLASS}
          >
            {PLACEMENT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Application">
          <select
            data-testid="pool-fwd-application"
            value={p.application}
            onChange={(e) =>
              p.setApplication(e.target.value as PoolApplication)
            }
            className={SELECT_CLASS}
          >
            {APPLICATION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {/* Winter / Heat-up / Target temp */}
      <div className="mt-3 grid grid-cols-3 gap-3">
        <Field label="Winter ops">
          <select
            data-testid="pool-fwd-winter"
            value={p.winterOp}
            onChange={(e) => p.setWinterOp(e.target.value as 'yes' | 'no')}
            className={SELECT_CLASS}
          >
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </Field>
        <Field label="Heat-up target">
          <select
            data-testid="pool-fwd-heattime"
            value={p.heatTimeTarget}
            onChange={(e) =>
              p.setHeatTimeTarget(Number(e.target.value) as 24 | 36 | 48)
            }
            className={SELECT_CLASS}
          >
            {HEAT_TIME_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Target temp">
          <select
            data-testid="pool-fwd-target"
            value={p.targetTemp}
            onChange={(e) =>
              p.setTargetTemp(Number(e.target.value) as 26 | 27 | 28 | 29 | 30)
            }
            className={SELECT_CLASS}
          >
            {TARGET_TEMP_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {t}°C
              </option>
            ))}
          </select>
        </Field>
      </div>

      <button
        type="button"
        onClick={p.onCalculate}
        disabled={p.loading}
        className="mt-4 w-full rounded-md bg-slate-900 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400 print:hidden"
        data-testid="pool-fwd-calculate"
      >
        {p.loading ? 'Calculating…' : 'Calculate & Generate ROI'}
      </button>
    </div>
  );
}

// ─── Forward result tabs ────────────────────────────────────────────────────

function ForwardResultsTabs({
  subTab,
  setSubTab,
}: {
  subTab: 'rec' | 'roi';
  setSubTab: (v: 'rec' | 'roi') => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Forward flow result pages"
      className="mb-3 flex gap-1 rounded-lg bg-stone-200 p-1 print:hidden"
    >
      <button
        type="button"
        role="tab"
        aria-selected={subTab === 'rec'}
        data-testid="pool-fwd-tab-rec"
        onClick={() => setSubTab('rec')}
        className={`flex-1 rounded-md px-3 py-2 text-center text-sm transition ${
          subTab === 'rec'
            ? 'bg-white font-bold text-slate-900 shadow-sm'
            : 'text-slate-600 hover:text-slate-900'
        }`}
      >
        <div>Page 1 · Recommendation</div>
        <div className="mt-0.5 text-[11px] font-normal text-slate-500">
          Sizing &amp; configuration
        </div>
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={subTab === 'roi'}
        data-testid="pool-fwd-tab-roi"
        onClick={() => setSubTab('roi')}
        className={`flex-1 rounded-md px-3 py-2 text-center text-sm transition ${
          subTab === 'roi'
            ? 'bg-white font-bold text-slate-900 shadow-sm'
            : 'text-slate-600 hover:text-slate-900'
        }`}
      >
        <div>Page 2 · ROI Analysis</div>
        <div className="mt-0.5 text-[11px] font-normal text-slate-500">
          Auto-attached to commercial quotes
        </div>
      </button>
    </div>
  );
}

// ─── Recommendation view (Page 1) ───────────────────────────────────────────

function RecommendationView({
  response,
  placement,
  application,
  targetTemp,
  winterOp,
}: {
  response: PoolCalculateResponse;
  placement: PoolPlacement;
  application: PoolApplication;
  targetTemp: number;
  winterOp: 'yes' | 'no';
}) {
  const { sizing, roi_full } = response;
  const opt1 = sizing.recommendation.option_1_no_redundancy;
  const opt2 = sizing.recommendation.option_2_with_redundancy;
  const upgraded = !!opt2.model_upgraded_from_option_1;

  return (
    <div data-testid="pool-rec-view" className="space-y-4">
      {/* Summary tiles */}
      <div className="grid grid-cols-3 gap-2">
        <SummaryTile
          label="Volume"
          value={`${fmt(sizing.pool.volume_m3, 1)} m³`}
        />
        <SummaryTile
          label="Heat load"
          value={`${fmt(sizing.load.required_capacity_kw, 1)} kW`}
        />
        <SummaryTile
          label="@ design"
          value={`${sizing.design_conditions.design_ambient_c}°C`}
        />
      </div>

      {sizing.recommendation.upgrade_reason && (
        <div
          data-testid="pool-upgrade-warning"
          className="rounded-md border-l-4 border-amber-600 bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
          <strong>↑ Model upgraded.</strong>{' '}
          {sizing.recommendation.upgrade_reason}
        </div>
      )}

      {/* Two option cards */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {/* Option 1 — standard slate border */}
        <div
          data-testid="pool-option-1"
          className="relative rounded-lg border border-slate-200 bg-white p-4"
        >
          <span className="absolute right-2 top-2 rounded bg-rose-50 px-1.5 py-0.5 font-mono text-[9.5px] font-bold uppercase tracking-wider text-rose-800">
            No Redundancy
          </span>
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
            Option 1 · Standard
          </div>
          <div className="mt-1 text-lg font-bold leading-tight">
            {opt1.qty_working === 1
              ? '1 Machine'
              : `${opt1.qty_working} Machines`}
          </div>
          <div className="text-xs text-slate-600">
            {opt1.qty_working} × {opt1.model_id}
          </div>
          <StockPill inStock={opt1.in_stock} />
          <div className="mt-2 grid grid-cols-2 gap-1 text-[11px]">
            <KvRow label="Output" value={`${fmt(opt1.total_output_kw, 1)} kW`} />
            <KvRow label="COP" value={fmt(opt1.cop_at_ambient, 2)} />
          </div>
          <HeatUpRow heatUp={opt1.estimated_heat_up} />
        </div>

        {/* Option 2 — emerald border 2px + "RECOMMENDED" badge if upgraded */}
        <div
          data-testid="pool-option-2"
          className="relative rounded-lg border-2 border-emerald-600 bg-gradient-to-b from-emerald-50 to-white p-4"
        >
          <span
            className={`absolute right-2 top-2 rounded px-1.5 py-0.5 font-mono text-[9.5px] font-bold uppercase tracking-wider ${
              upgraded
                ? 'bg-amber-300 text-amber-900'
                : 'bg-emerald-100 text-emerald-800'
            }`}
            data-testid={upgraded ? 'pool-option-2-upgraded-badge' : undefined}
          >
            {upgraded ? 'Recommended · Upgraded' : 'Dual Mode'}
          </span>
          <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">
            Option 2 · Variable Load
          </div>
          {upgraded ? (
            <>
              <div className="mt-1 text-lg font-bold leading-tight">
                2 × {opt2.model_id}
              </div>
              <div className="text-xs text-slate-600">
                Mutual Redundancy — either machine alone covers full load
              </div>
              <StockPill inStock={opt2.in_stock} />
              <div className="mt-2 grid grid-cols-2 gap-1 text-[11px]">
                <KvRow
                  label="Total cap"
                  value={`${fmt(opt2.total_output_kw, 1)} kW`}
                />
                <KvRow
                  label="Peak (both)"
                  value={`${fmt(opt2.total_output_kw, 1)} kW`}
                />
              </div>
              <HeatUpRow heatUp={opt2.estimated_heat_up} />
            </>
          ) : (
            <>
              <div className="mt-1 text-lg font-bold leading-tight">
                {opt2.qty_total} Machines · Peak Boost + Standby
              </div>
              <div className="text-xs text-slate-600">
                {opt2.qty_working}W + {opt2.qty_standby}S × {opt2.model_id}
              </div>
              <StockPill inStock={opt2.in_stock} />
              <div className="mt-3 rounded-md border-l-[3px] border-emerald-600 bg-white/70 px-2.5 py-2">
                <div className="font-mono text-[10px] font-bold uppercase text-emerald-700">
                  ⚡ Peak Boost · {opt2.qty_total} parallel
                </div>
                <div className="mt-1 flex justify-between text-[11.5px]">
                  <span>
                    {fmt(
                      opt2.capacity_per_unit_at_ambient_kw * opt2.qty_total,
                      1,
                    )}{' '}
                    kW (peak)
                  </span>
                  <span className="font-mono font-bold">
                    {Math.ceil(opt2.estimated_heat_up.hours_low / 2)}–
                    {Math.ceil(opt2.estimated_heat_up.hours_high / 2)} hr
                  </span>
                </div>
              </div>
              <div className="mt-2 rounded-md border-l-[3px] border-blue-700 bg-white/70 px-2.5 py-2">
                <div className="font-mono text-[10px] font-bold uppercase text-blue-700">
                  ⟳ Normal · {opt2.qty_working}W + {opt2.qty_standby}S alternating
                </div>
                <div className="mt-1 flex justify-between text-[11.5px]">
                  <span>{fmt(opt2.total_output_kw, 1)} kW</span>
                  <span className="font-mono font-bold">
                    {opt2.estimated_heat_up.hours_low}–
                    {opt2.estimated_heat_up.hours_high} hr
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Sales narrative callout */}
      <div className="rounded-lg border border-stone-200 bg-gradient-to-br from-emerald-50 to-blue-50 p-3">
        <div className="font-mono text-[10.5px] font-bold uppercase tracking-wide text-slate-700">
          {upgraded ? 'Why Option 2 upgrades the model' : 'Why pick Option 2?'}
        </div>
        <div className="mt-1 text-xs leading-relaxed text-slate-700">
          {upgraded ? (
            <>
              Instead of {opt1.qty_working + 1} × {opt1.model_id} (
              {opt1.qty_working} working + 1 standby ={' '}
              {opt1.qty_working + 1} machines), Option 2 uses 2 × {opt2.model_id}.
              Same peak capacity, half the machines, fewer pumps, simpler cascade.
              Either machine alone covers the full load — when one is in service,
              the other carries the pool.
            </>
          ) : (
            <>
              Option 2 gives <strong>{opt2.qty_total}× peak capacity on demand</strong>{' '}
              — run all units in parallel for fast pre-opening heat-up, big party
              refill, or unexpected cold snap. Cascade panel auto-alternates them
              in normal operation for even wear and fault coverage.
            </>
          )}
        </div>
      </div>

      {/* Cooling-bonus chip (when present) */}
      {roi_full.cooling_bonus_applies && roi_full.cooling_bonus_per_year > 0 && (
        <div
          data-testid="pool-cooling-bonus"
          className="rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-900"
        >
          Cooling savings: {rs(roi_full.cooling_bonus_per_year)}/yr (outdoor pool, warm climate)
        </div>
      )}

      {/* Page 2 nudge */}
      <div className="rounded-md border-l-[3px] border-blue-700 bg-blue-50 px-3 py-2 text-xs leading-relaxed text-blue-900 print:hidden">
        → <strong>Page 2 · ROI Analysis</strong> auto-attached to this commercial
        quotation. Tap the tab above to view savings vs LPG, Diesel, and Electric.
      </div>

      {/* Built-in features */}
      <BuiltInFeaturesPanel features={sizing.builtin_features} />

      {/* Engineering notes accordion */}
      <EngineeringNotesPanel notes={sizing.engineering_notes} />

      {/* Tiny context strip */}
      <div className="pt-1 text-[11px] text-slate-500">
        {sizing.pool.length_m}×{sizing.pool.width_m}×{sizing.pool.depth_m} m ·{' '}
        {sizing.design_conditions.climate_name} · {placement} · {application} ·
        target {targetTemp}°C · heat-up ≤ ? hr
        {winterOp === 'yes' ? ' · year-round' : ''}
      </div>
    </div>
  );
}

// ─── ROI page (Page 2 — auto-attached) ──────────────────────────────────────

function RoiPageView({
  response,
  application,
}: {
  response: PoolCalculateResponse;
  application: PoolApplication;
}) {
  const { sizing, roi_full } = response;
  const opt2 = sizing.recommendation.option_2_with_redundancy;
  const c = roi_full.year_1_costs;
  const s = roi_full.annual_savings_year_1;
  const maxSav = Math.max(s.vs_lpg, s.vs_diesel, s.vs_electric, 1);

  return (
    <div
      data-testid="pool-roi-view"
      className="pool-print-roi-page space-y-4"
    >
      {/* Header */}
      <div className="rounded-lg bg-gradient-to-br from-slate-900 to-slate-700 p-4 text-white">
        <div className="font-mono text-[10px] font-bold uppercase tracking-widest opacity-60">
          Quotation · Page 2 · ROI Analysis
        </div>
        <div className="mt-1 font-serif text-xl font-bold leading-tight">
          {opt2.qty_working} × {opt2.model_id} ·{' '}
          {application.charAt(0).toUpperCase() + application.slice(1)} Project
        </div>
        <div className="mt-1 text-xs opacity-90">
          {roi_full.inputs_used.design_ambient_c}°C design ·{' '}
          {fmt(sizing.pool.volume_m3, 1)} m³ · target{' '}
          {roi_full.inputs_used.target_temp_c}°C
        </div>
      </div>

      {/* 1. Operating Profile */}
      <Section title="1. Operating Profile">
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
          <ProfileRow
            label="Days operational/year"
            value={`${roi_full.operating_profile.days_per_year}`}
            sub={`(${roi_full.operating_profile.derived_from})`}
            warn={roi_full.operating_profile.derived_from === 'custom'}
          />
          <ProfileRow
            label="Hours/day at temp"
            value={`${roi_full.operating_profile.hours_per_day} hrs`}
          />
          <ProfileRow
            label="Heat-up frequency"
            value={roi_full.operating_profile.heat_up_frequency}
          />
          <ProfileRow
            label="Annual heat to pool"
            value={`${fmt(roi_full.operating_profile.annual_heat_delivered_kwh, 0)} kWh`}
          />
        </div>
        <div className="mt-2 border-t border-stone-200 pt-2 text-[11px] text-slate-500">
          {roi_full.operating_profile.derivation_note}
        </div>
      </Section>

      {/* 2. Year-1 Cost Comparison */}
      <Section title="2. Year-1 Cost Comparison">
        <table className="w-full text-[11.5px]">
          <thead>
            <tr className="border-b border-stone-200 text-left font-mono text-[9.5px] uppercase tracking-wider text-slate-500">
              <th className="py-2"></th>
              <th className="py-2 text-center text-emerald-700 bg-emerald-50">
                Heat Pump
              </th>
              <th className="py-2 text-center">LPG</th>
              <th className="py-2 text-center">Diesel</th>
              <th className="py-2 text-center">Electric</th>
            </tr>
          </thead>
          <tbody>
            <CmpRow
              label="Efficiency / COP"
              hp={`${roi_full.annual_avg_cop.toFixed(1)} avg`}
              lpg="85%"
              diesel="80%"
              electric="95%"
            />
            <CmpRow
              label="Year-1 energy"
              hp={rs(c.heat_pump.annual_cost)}
              lpg={rs(c.lpg_boiler.annual_cost)}
              diesel={rs(c.diesel_boiler.annual_cost)}
              electric={rs(c.electric_immersion.annual_cost)}
            />
            <CmpRow
              label="Monthly energy"
              hp={rs(c.heat_pump.monthly_cost)}
              lpg={rs(c.lpg_boiler.monthly_cost)}
              diesel={rs(c.diesel_boiler.monthly_cost)}
              electric={rs(c.electric_immersion.monthly_cost)}
            />
            <CmpRow
              label="Maintenance/yr"
              hp={rs(c.heat_pump.maintenance_per_year)}
              lpg={rs(c.lpg_boiler.maintenance_per_year)}
              diesel={rs(c.diesel_boiler.maintenance_per_year)}
              electric={rs(c.electric_immersion.maintenance_per_year)}
            />
            <CmpRow
              label="Capex (indicative)"
              hp={rs(c.heat_pump.capex)}
              lpg={rs(c.lpg_boiler.capex)}
              diesel={rs(c.diesel_boiler.capex)}
              electric={rs(c.electric_immersion.capex)}
            />
          </tbody>
        </table>
      </Section>

      {/* 3. Annual Savings (Year-1) */}
      <Section title="3. Annual Savings (Year-1)">
        <SavingsRow
          label="vs LPG Boiler"
          value={s.vs_lpg}
          pct={(s.vs_lpg / maxSav) * 100}
          color="#92400E"
          payback={s.payback_years.vs_lpg}
        />
        <SavingsRow
          label="vs Diesel Boiler"
          value={s.vs_diesel}
          pct={(s.vs_diesel / maxSav) * 100}
          color="#7C2D12"
          payback={s.payback_years.vs_diesel}
        />
        <SavingsRow
          label="vs Electric Immersion"
          value={s.vs_electric}
          pct={(s.vs_electric / maxSav) * 100}
          color="#1E40AF"
          payback={s.payback_years.vs_electric}
        />
      </Section>

      {/* 4. 5-Year TCO */}
      <TcoSection
        title="4. 5-Year Total Cost of Ownership"
        years={5}
        tco={roi_full.tco_5yr}
      />

      {/* 5. 10-Year TCO */}
      <TcoSection
        title="5. 10-Year Total Cost of Ownership"
        years={10}
        tco={roi_full.tco_10yr}
      />

      {/* Compounding callout */}
      {roi_full.tco_5yr.net_savings_vs_lpg > 0 &&
        roi_full.tco_10yr.net_savings_vs_lpg > 0 && (
          <div className="rounded-lg border border-stone-200 bg-gradient-to-br from-emerald-50 to-blue-50 p-3">
            <div className="font-mono text-[10.5px] font-bold uppercase tracking-wide text-slate-700">
              Heat pump advantage compounds
            </div>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <div className="rounded-md bg-white p-2.5">
                <div className="text-[10.5px] font-semibold uppercase text-slate-500">
                  5-yr saving vs LPG
                </div>
                <div className="mt-1 font-mono text-lg font-bold text-emerald-700">
                  {rs(roi_full.tco_5yr.net_savings_vs_lpg)}
                </div>
              </div>
              <div className="rounded-md bg-white p-2.5">
                <div className="text-[10.5px] font-semibold uppercase text-slate-500">
                  10-yr saving vs LPG
                </div>
                <div className="mt-1 font-mono text-lg font-bold text-emerald-700">
                  {rs(roi_full.tco_10yr.net_savings_vs_lpg)}
                </div>
              </div>
            </div>
            <div className="mt-2 text-[11.5px] text-slate-600">
              10-year saving ={' '}
              <strong>
                {(
                  roi_full.tco_10yr.net_savings_vs_lpg /
                  roi_full.tco_5yr.net_savings_vs_lpg
                ).toFixed(1)}
                ×
              </strong>{' '}
              the 5-year — fuel inflation compounds yearly.
            </div>
          </div>
        )}

      {/* 6. Cooling bonus */}
      {roi_full.cooling_bonus_applies && (
        <Section title="6. Cooling-Season Bonus">
          <div className="text-sm font-semibold">
            Reverse-cycle pool cooling:{' '}
            <span className="font-mono text-emerald-700">
              {rs(roi_full.cooling_bonus_per_year)}/yr
            </span>
          </div>
          <div className="mt-1 text-[11.5px] text-slate-600">
            {roi_full.disclaimer.cooling_note}
          </div>
        </Section>
      )}

      {/* Methodology */}
      <div className="rounded-md border-l-[3px] border-slate-400 bg-stone-100 px-3 py-2 text-[11px] leading-relaxed text-slate-700">
        <div className="font-mono font-bold uppercase tracking-wide text-slate-900">
          Methodology
        </div>
        <ul className="mt-1 list-disc pl-5">
          <li>{roi_full.disclaimer.cover_assumed}</li>
          <li>{roi_full.disclaimer.cop_basis}</li>
          <li>{roi_full.disclaimer.escalation_basis}</li>
          <li>{roi_full.disclaimer.days_basis}</li>
          <li>{roi_full.disclaimer.capex_basis}</li>
        </ul>
      </div>
    </div>
  );
}

// ─── Reverse-flow inputs panel ──────────────────────────────────────────────

interface ReverseFlowProps {
  model: string;
  setModel: (v: string) => void;
  climate: PoolClimate;
  setClimate: (v: PoolClimate) => void;
  placement: PoolPlacement;
  setPlacement: (v: PoolPlacement) => void;
  winterOp: 'yes' | 'no';
  setWinterOp: (v: 'yes' | 'no') => void;
  qty: number;
  setQty: (v: number) => void;
  length: number | '';
  setLength: (v: number | '') => void;
  width: number | '';
  setWidth: (v: number | '') => void;
  depthAvg: number | '';
  setDepthAvg: (v: number | '') => void;
  unit: PoolUnit;
  setUnit: (v: PoolUnit) => void;
  loading: boolean;
  onCalculate: () => void;
}

function ReverseFlow(p: ReverseFlowProps) {
  return (
    <div className="rounded-lg bg-white p-5 shadow-sm">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="Pool heat pump model">
          <select
            data-testid="pool-rev-model"
            value={p.model}
            onChange={(e) => p.setModel(e.target.value)}
            className={SELECT_CLASS}
          >
            {POOL_MODEL_OPTIONS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Qty">
          <select
            data-testid="pool-rev-qty"
            value={p.qty}
            onChange={(e) => p.setQty(Number(e.target.value))}
            className={SELECT_CLASS}
          >
            {[1, 2, 3].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Climate zone">
          <select
            data-testid="pool-rev-climate"
            value={p.climate}
            onChange={(e) => p.setClimate(e.target.value as PoolClimate)}
            className={SELECT_CLASS}
          >
            {CLIMATE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Placement">
          <select
            data-testid="pool-rev-placement"
            value={p.placement}
            onChange={(e) => p.setPlacement(e.target.value as PoolPlacement)}
            className={SELECT_CLASS}
          >
            {PLACEMENT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Winter ops">
          <select
            data-testid="pool-rev-winter"
            value={p.winterOp}
            onChange={(e) => p.setWinterOp(e.target.value as 'yes' | 'no')}
            className={SELECT_CLASS}
          >
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </Field>
      </div>

      <div className="mt-4">
        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
          Optional · fit check
        </div>
        <div className="grid grid-cols-4 gap-2">
          <Field label="Length">
            <input
              type="number"
              step={0.1}
              data-testid="pool-rev-length"
              value={p.length}
              onChange={(e) =>
                p.setLength(e.target.value === '' ? '' : Number(e.target.value))
              }
              className={INPUT_CLASS}
            />
          </Field>
          <Field label="Width">
            <input
              type="number"
              step={0.1}
              data-testid="pool-rev-width"
              value={p.width}
              onChange={(e) =>
                p.setWidth(e.target.value === '' ? '' : Number(e.target.value))
              }
              className={INPUT_CLASS}
            />
          </Field>
          <Field label="Depth">
            <input
              type="number"
              step={0.1}
              data-testid="pool-rev-depth"
              value={p.depthAvg}
              onChange={(e) =>
                p.setDepthAvg(e.target.value === '' ? '' : Number(e.target.value))
              }
              className={INPUT_CLASS}
            />
          </Field>
          <Field label="Unit">
            <select
              data-testid="pool-rev-unit"
              value={p.unit}
              onChange={(e) => p.setUnit(e.target.value as PoolUnit)}
              className={SELECT_CLASS}
            >
              <option value="m">m</option>
              <option value="ft">ft</option>
            </select>
          </Field>
        </div>
      </div>

      <button
        type="button"
        onClick={p.onCalculate}
        disabled={p.loading}
        className="mt-4 w-full rounded-md bg-slate-900 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
        data-testid="pool-rev-calculate"
      >
        {p.loading ? 'Loading…' : 'Show capability envelope'}
      </button>
    </div>
  );
}

// ─── Reverse result view ────────────────────────────────────────────────────

function ReverseResultView({ result }: { result: PoolModelEnvelopeResult }) {
  const grid = result.max_pool_volume_grid;
  // Flat heatmap intensity scale.
  const flat = grid.values_m3
    .flat()
    .filter((v): v is number => typeof v === 'number');
  const maxVol = flat.length ? Math.max(...flat, 1) : 1;

  return (
    <div className="space-y-4">
      {/* Configuration brief */}
      <div className="rounded-lg bg-white p-5 shadow-sm">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
          Capability brief
        </div>
        <div className="mt-1 text-lg font-bold">
          {result.configuration.qty_working} × {result.configuration.model_id}
        </div>
        <div className="mt-1 text-xs text-slate-600">
          {result.design_conditions.climate_name} ·{' '}
          {result.design_conditions.placement} · design{' '}
          {result.design_conditions.design_ambient_c}°C
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
          <SummaryTile
            label="Total cap (27°C)"
            value={`${fmt(result.configuration.total_capacity_at_27c_kw, 1)} kW`}
          />
          <SummaryTile
            label="At design"
            value={`${fmt(result.configuration.total_capacity_at_design_kw, 1)} kW`}
          />
          <SummaryTile
            label="COP @ design"
            value={fmt(result.configuration.cop_at_design, 2)}
          />
          <SummaryTile
            label="Total power"
            value={`${fmt(result.configuration.total_power_kw, 1)} kW`}
          />
          <SummaryTile
            label="Total flow"
            value={`${fmt(result.configuration.total_flow_m3hr, 1)} m³/h`}
          />
          <SummaryTile
            label="In stock"
            value={result.configuration.in_stock ? 'Yes' : 'Made-to-order'}
          />
        </div>
      </div>

      {/* Max-volume grid */}
      <div
        className="rounded-lg bg-white p-5 shadow-sm"
        data-testid="pool-envelope-grid"
      >
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
          Max pool volume (m³) · heat-up target × target temperature
        </div>
        <div className="mt-1 text-[11px] text-slate-500">{grid.assumption}</div>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[420px] font-mono text-xs">
            <thead>
              <tr className="bg-stone-100 text-slate-600">
                <th className="border border-stone-200 px-2 py-1 text-left">
                  heat-up ↓ / target →
                </th>
                {grid.target_temps_c.map((t) => (
                  <th
                    key={t}
                    className="border border-stone-200 px-2 py-1 text-center"
                  >
                    {t}°C
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.heat_time_targets_h.map((h, hi) => (
                <tr key={h}>
                  <th className="border border-stone-200 bg-stone-100 px-2 py-1 text-left font-semibold text-slate-700">
                    {h} hr
                  </th>
                  {grid.target_temps_c.map((_t, ti) => {
                    const v = grid.values_m3[hi]?.[ti];
                    const display = typeof v === 'number' ? fmt(v, 0) : '—';
                    const intensity =
                      typeof v === 'number' ? Math.min(1, v / maxVol) : 0;
                    return (
                      <td
                        key={ti}
                        className="border border-stone-200 px-2 py-1 text-center font-semibold text-slate-900"
                        style={{
                          background: `rgba(22, 101, 52, ${intensity * 0.3})`,
                        }}
                      >
                        {display}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sweet spot */}
      {result.sweet_spot && (
        <div className="rounded-md border-l-[3px] border-emerald-600 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
          <strong>Sweet spot · </strong>
          {result.sweet_spot.summary}
        </div>
      )}

      {/* Operating limits */}
      <div className="rounded-lg bg-white p-5 shadow-sm">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
          Operating limits
        </div>
        <dl className="mt-2 grid grid-cols-1 gap-2 text-xs md:grid-cols-2">
          {result.operating_limits.coldest_ambient_for_36h_typical_pool_c !== null && (
            <div>
              <dt className="text-slate-500">
                Coldest ambient (36h, ~
                {result.operating_limits.coldest_ambient_assumed_volume_m3} m³)
              </dt>
              <dd className="font-mono font-bold">
                {result.operating_limits.coldest_ambient_for_36h_typical_pool_c}°C
              </dd>
            </div>
          )}
          <div>
            <dt className="text-slate-500">Max maintenance load</dt>
            <dd className="font-mono font-bold">
              {result.operating_limits.max_maintenance_load_kw} kW
            </dd>
          </div>
        </dl>
        <ul className="mt-3 list-disc pl-5 text-[11px] text-slate-600">
          {result.operating_limits.notes.map((n, i) => (
            <li key={i}>{n}</li>
          ))}
        </ul>
      </div>

      {/* Fit check (only when caller passed pool_inputs) */}
      {result.fit && <FitCheckCard fit={result.fit} />}
    </div>
  );
}

function FitCheckCard({
  fit,
}: {
  fit: NonNullable<PoolModelEnvelopeResult['fit']>;
}) {
  const palette: Record<typeof fit.status, { bg: string; text: string; border: string; label: string }> = {
    undersized: {
      bg: 'bg-rose-50',
      text: 'text-rose-800',
      border: 'border-rose-300',
      label: 'Undersized',
    },
    tight_fit: {
      bg: 'bg-amber-50',
      text: 'text-amber-800',
      border: 'border-amber-300',
      label: 'Tight fit',
    },
    good_fit: {
      bg: 'bg-emerald-50',
      text: 'text-emerald-800',
      border: 'border-emerald-300',
      label: 'Good fit',
    },
    oversized: {
      bg: 'bg-blue-50',
      text: 'text-blue-800',
      border: 'border-blue-300',
      label: 'Oversized',
    },
  };
  const p = palette[fit.status];
  return (
    <div
      data-testid="pool-fit-check"
      className={`rounded-lg border ${p.border} ${p.bg} p-4 ${p.text}`}
    >
      <div className="text-[11px] font-bold uppercase tracking-wider">
        Fit check · {p.label}
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
        <KvRow label="Pool volume" value={`${fit.pool_volume_m3} m³`} />
        <KvRow label="Required" value={`${fit.required_capacity_kw} kW`} />
        <KvRow label="Available" value={`${fit.available_capacity_kw} kW`} />
        <KvRow label="Headroom" value={`${fit.headroom_percent.toFixed(0)}%`} />
      </div>
      <div className="mt-3 text-xs leading-relaxed">{fit.explanation}</div>
    </div>
  );
}

// ─── Small primitives ──────────────────────────────────────────────────────

const SELECT_CLASS =
  'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200';
const INPUT_CLASS =
  'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium text-slate-600">{label}</label>
      {children}
    </div>
  );
}

function LeadField({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  testId,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  testId?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-slate-600">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
        data-testid={testId}
      />
    </div>
  );
}

function SummaryTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-stone-100 px-2.5 py-2">
      <div className="text-[10px] font-medium uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div className="mt-1 text-base font-bold text-slate-900">{value}</div>
    </div>
  );
}

function StockPill({ inStock }: { inStock: boolean }) {
  return (
    <span
      data-testid={inStock ? 'pool-stock-in' : 'pool-stock-non'}
      className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold ${
        inStock
          ? 'bg-emerald-100 text-emerald-800'
          : 'bg-amber-100 text-amber-800'
      }`}
    >
      {inStock ? '● In stock' : '⏱ Non-stock'}
    </span>
  );
}

function KvRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between rounded bg-black/5 px-2 py-1">
      <span className="text-slate-500">{label}</span>
      <span className="font-mono font-semibold">{value}</span>
    </div>
  );
}

function HeatUpRow({
  heatUp,
}: {
  heatUp: PoolConfiguration['estimated_heat_up'];
}) {
  return (
    <div className="mt-2 flex justify-between rounded-md bg-blue-50 px-2.5 py-1.5">
      <span className="text-[10px] font-bold uppercase text-blue-700">
        Heat-up
      </span>
      <span className="font-mono text-xs font-bold">
        {heatUp.hours_low}–{heatUp.hours_high} hr
        {heatUp.within_target ? ' ✓' : ''}
      </span>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg bg-white p-4 shadow-sm">
      <h3 className="mb-2 text-sm font-bold text-slate-900">{title}</h3>
      {children}
    </div>
  );
}

function ProfileRow({
  label,
  value,
  sub,
  warn,
}: {
  label: string;
  value: string;
  sub?: string;
  warn?: boolean;
}) {
  return (
    <>
      <div className="text-slate-600">{label}</div>
      <div className="text-right font-mono font-semibold">
        {value}
        {sub && (
          <span
            className={`ml-1 text-[10px] ${warn ? 'text-amber-700' : 'text-slate-400'}`}
          >
            {sub}
          </span>
        )}
      </div>
    </>
  );
}

function CmpRow({
  label,
  hp,
  lpg,
  diesel,
  electric,
}: {
  label: string;
  hp: string;
  lpg: string;
  diesel: string;
  electric: string;
}) {
  return (
    <tr className="border-b border-stone-100 last:border-none">
      <td className="py-1.5 text-slate-600">{label}</td>
      <td className="bg-emerald-50 py-1.5 text-center font-mono font-semibold text-emerald-900">
        {hp}
      </td>
      <td className="py-1.5 text-center font-mono">{lpg}</td>
      <td className="py-1.5 text-center font-mono">{diesel}</td>
      <td className="py-1.5 text-center font-mono">{electric}</td>
    </tr>
  );
}

function SavingsRow({
  label,
  value,
  pct,
  color,
  payback,
}: {
  label: string;
  value: number;
  pct: number;
  color: string;
  payback: number | null;
}) {
  return (
    <div className="mb-2 last:mb-0">
      <div className="flex justify-between">
        <span className="text-xs font-medium">{label}</span>
        <span className="font-mono text-sm font-bold">
          {rs(value)}
          <span className="ml-1 text-[10px] text-slate-500">/yr</span>
        </span>
      </div>
      <div className="mt-1 h-2 overflow-hidden rounded-full bg-stone-200">
        <div
          className="h-full"
          style={{
            width: `${Math.max(0, pct).toFixed(0)}%`,
            background: color,
          }}
        />
      </div>
      {payback !== null && payback >= 0 && (
        <div className="mt-1 text-[11px] text-slate-500">
          Capex payback:{' '}
          <strong>
            {payback <= 0 ? 'immediate' : `${payback.toFixed(1)} yrs`}
          </strong>
        </div>
      )}
    </div>
  );
}

function TcoSection({
  title,
  years,
  tco,
}: {
  title: string;
  years: number;
  tco: {
    heat_pump: number;
    lpg_boiler: number;
    diesel_boiler: number;
    electric_immersion: number;
    net_savings_vs_lpg: number;
  };
}) {
  const max = Math.max(
    tco.heat_pump,
    tco.lpg_boiler,
    tco.diesel_boiler,
    tco.electric_immersion,
  );
  const rows: Array<[string, number, string, boolean]> = [
    ['Sun Stellar Heat Pump', tco.heat_pump, '#16a34a', true],
    ['LPG Boiler', tco.lpg_boiler, '#D97706', false],
    ['Diesel Boiler', tco.diesel_boiler, '#7C2D12', false],
    ['Electric Immersion', tco.electric_immersion, '#1E40AF', false],
  ];
  return (
    <Section title={title}>
      <table className="mb-3 w-full text-[11.5px]">
        <tbody>
          <tr className="bg-slate-900 font-bold text-white">
            <td className="py-2 pl-3">Total {years}-Yr TCO</td>
            <td className="bg-emerald-700 py-2 text-center font-mono">
              {rs(tco.heat_pump)} ✓
            </td>
            <td className="py-2 text-center font-mono">{rs(tco.lpg_boiler)}</td>
            <td className="py-2 text-center font-mono">
              {rs(tco.diesel_boiler)}
            </td>
            <td className="py-2 text-center font-mono">
              {rs(tco.electric_immersion)}
            </td>
          </tr>
        </tbody>
      </table>
      {rows.map(([lbl, val, col, win]) => {
        const pct = (val / max) * 100;
        return (
          <div key={lbl} className="mb-2 last:mb-0">
            <div className="flex justify-between">
              <span className={`text-xs ${win ? 'font-bold' : ''}`}>
                {lbl}
                {win && ' ✓ best'}
              </span>
              <span className="font-mono text-sm font-bold">{rs(val)}</span>
            </div>
            <div className="mt-1 h-2 overflow-hidden rounded-full bg-stone-200">
              <div
                className="h-full"
                style={{ width: `${pct.toFixed(0)}%`, background: col }}
              />
            </div>
          </div>
        );
      })}
      {tco.net_savings_vs_lpg > 0 && (
        <div className="mt-2 border-t border-stone-200 pt-2 text-xs">
          {years}-yr net savings vs LPG:{' '}
          <strong className="font-mono text-emerald-700">
            {rs(tco.net_savings_vs_lpg)}
          </strong>
        </div>
      )}
    </Section>
  );
}

function BuiltInFeaturesPanel({
  features,
}: {
  features: PoolCalculateResponse['sizing']['builtin_features'];
}) {
  const items = useMemo(
    () =>
      (features ?? []).map((f) =>
        typeof f === 'string' ? { title: f, description: '' } : f,
      ),
    [features],
  );
  if (!items.length) return null;
  return (
    <details className="rounded-lg border border-stone-200">
      <summary className="cursor-pointer rounded-lg bg-stone-50 px-3 py-2 text-xs font-semibold text-slate-700">
        Built-in features ({items.length})
      </summary>
      <ul className="space-y-2 px-4 py-3 text-xs leading-relaxed text-slate-700">
        {items.map((f, i) => (
          <li key={i}>
            <strong>{f.title}</strong>
            {f.description ? `: ${f.description}` : ''}
          </li>
        ))}
      </ul>
    </details>
  );
}

function EngineeringNotesPanel({
  notes,
}: {
  notes: PoolCalculateResponse['sizing']['engineering_notes'];
}) {
  const items = useMemo(
    () =>
      (notes ?? []).map((n) =>
        typeof n === 'string' ? { title: n, description: '' } : n,
      ),
    [notes],
  );
  if (!items.length) return null;
  return (
    <details className="rounded-lg border border-stone-200">
      <summary className="cursor-pointer rounded-lg bg-stone-50 px-3 py-2 text-xs font-semibold text-slate-700">
        Engineering notes ({items.length})
      </summary>
      <ol className="space-y-2 px-4 py-3 text-xs leading-relaxed text-slate-700">
        {items.map((n, i) => (
          <li key={i}>
            <strong>{n.title}</strong>
            {n.description ? `: ${n.description}` : ''}
          </li>
        ))}
      </ol>
    </details>
  );
}

export default PoolCalculatorPage;
